import { Controller } from "stimulus"

export default class extends Controller {
  // The select this picker fills, named by the card rather than baked in here, so
  // more than one Downloads card can use the same controller.
  static values = { targetSelectId: String }

  connect() {
    // Listen for the custom event
    document.addEventListener("updateCompanyAddresses", this.updateSelect);
  }

  disconnect() {
    // Clean up the event listener
    document.removeEventListener("updateCompanyAddresses", this.updateSelect);
  }

  fetchAddresses(event) {
    const companyId = event.target.value;

    if (companyId) {
      fetch(`/companies/${companyId}/company_addresses.json`)
        .then(response => {
          if (!response.ok) throw new Error('Bad response');
          return response.json();
        })
        .then(data => {
          let options = data.addresses.map((address) => ({
            id: address.id,
            name: address.name
          }));

          options.unshift({ id: 0, name: 'All' });

          const updateEvent = new CustomEvent('updateCompanyAddresses', {
            detail: {
              addresses: options,
              targetSelectId: this.targetSelectIdValue
            }
          });
          document.dispatchEvent(updateEvent);
        })
        .catch(error => {
          console.error("Error fetching company addresses:", error);
        });
    } else {
      const clearEvent = new CustomEvent('updateCompanyAddresses', {
        detail: {
          addresses: [],
          targetSelectId: this.targetSelectIdValue
        }
      });
      document.dispatchEvent(clearEvent);
    }
  }

  updateSelect = (event) => {
    const { addresses, targetSelectId } = event.detail;

    // The two cards render this picker differently — Monthly Trip Groupings with a native
    // multi-select, Passenger Bookings with a DynamicSelect — so branch on what is on the
    // page rather than splitting the controller. The React one is found by a wrapper named
    // after the same hidden input, which is the only extra convention it costs.
    const mount = document.getElementById(`${targetSelectId}_mount`);
    if (mount) {
      this.updateReactSelect(mount, targetSelectId, addresses);
      return;
    }

    const select = document.getElementById(targetSelectId);
    if (select) {
      // Clear current options
      select.innerHTML = "";

      // Add new options
      addresses.forEach(address => {
        const option = document.createElement("option");
        option.value = address.id;
        option.text = address.name;
        select.appendChild(option);
      });
    }
  }

  // Re-renders a DynamicSelect against the newly fetched addresses.
  //
  // Unmount before mount, rather than re-rendering in place: the component holds its
  // selection in React state, so a plain re-render would leave the previous company's
  // addresses ticked above the new company's list. Tearing it down also clears the hidden
  // input, which is what the form actually posts — otherwise a company change could submit
  // addresses belonging to the company that was just replaced.
  //
  // @param {HTMLElement} mount wrapper holding the react-rails mount node
  // @param {String} targetSelectId id of the hidden input the select writes into
  // @param {Array} addresses [{id, name}] as fetched, "All" already prepended
  updateReactSelect(mount, targetSelectId, addresses) {
    const node = mount.querySelector("[data-react-class]");
    if (!node) return;

    const payload = JSON.parse(node.getAttribute("data-react-props") || "{}");
    payload.props = {
      ...payload.props,
      options: addresses.map(address => ({ value: address.id, label: address.name }))
    };
    node.setAttribute("data-react-props", JSON.stringify(payload));

    const hiddenInput = document.getElementById(targetSelectId);
    if (hiddenInput) hiddenInput.value = "";

    window.ReactRailsUJS.unmountComponents(mount);
    window.ReactRailsUJS.mountComponents(mount);
  }
}
